import { useCallback, useEffect, useMemo, useState } from 'react';
import { prefectures, regionMapping, regionOptions } from '../../../utils/prefectures';
import {
  normalizeSelectionArray,
  normalizeSelectionValue,
} from './artistEventListUtils';

export default function useArtistEventListFiltersState({ genres }) {
  const [filterStartDate, setFilterStartDate] = useState('');
  const [filterEndDate, setFilterEndDate] = useState('');
  const [autoRange, setAutoRange] = useState('auto');
  const [showSearchPopup, setShowSearchPopup] = useState(false);
  const [filterEventName, setFilterEventName] = useState('');
  const [filterGenres, setFilterGenres] = useState([]);
  const [filterRegions, setFilterRegions] = useState([]);
  const [filterPrefectures, setFilterPrefectures] = useState([]);
  const [filterVenues, setFilterVenues] = useState([]);
  const [showVenueFilterPopup, setShowVenueFilterPopup] = useState(false);
  const [showGenreFilterPopup, setShowGenreFilterPopup] = useState(false);
  const [showRegionFilterPopup, setShowRegionFilterPopup] = useState(false);
  const [showPrefectureFilterPopup, setShowPrefectureFilterPopup] = useState(false);
  const [genreFilterSearch, setGenreFilterSearch] = useState('');
  const [prefectureFilterSearch, setPrefectureFilterSearch] = useState('');
  const [allVenues, setAllVenues] = useState([]);
  const [venueFilterSearch, setVenueFilterSearch] = useState('');
  const [venueFilterPrefecture, setVenueFilterPrefecture] = useState('');
  const [sortOrder, setSortOrder] = useState('asc');

  const venuesById = useMemo(() => {
    const m = new Map();
    (allVenues || []).forEach((v) => {
      if (v?.id != null) m.set(String(v.id), v);
    });
    return m;
  }, [allVenues]);

  useEffect(() => {
    if (!venuesById.size) return;
    setFilterVenues((prev) => {
      const normalized = normalizeSelectionArray(prev);
      const next = normalized.filter((id) => venuesById.has(String(id)));
      return next.length === normalized.length ? prev : next;
    });
  }, [venuesById]);

  const genreMap = useMemo(() => {
    const map = new Map();
    (genres || []).forEach((genre) => {
      if (genre?.value == null) return;
      map.set(String(genre.value), genre);
    });
    return map;
  }, [genres]);

  const regionSelectionOptions = useMemo(
    () => regionOptions.filter((opt) => opt.value),
    []
  );

  const regionLabelMap = useMemo(
    () => new Map(regionSelectionOptions.map((opt) => [opt.value, opt.label])),
    [regionSelectionOptions]
  );

  const prefectureRegionMap = useMemo(() => {
    const map = {};
    Object.entries(regionMapping).forEach(([region, prefs]) => {
      prefs.forEach((pref) => {
        map[pref] = region;
      });
    });
    return map;
  }, []);

  const prefectureOptions = useMemo(
    () =>
      (prefectures || []).map((pref) => {
        const value = pref.value || pref;
        const label = pref.label || pref.value || pref;
        return {
          value,
          label,
          meta: prefectureRegionMap[value] || '',
        };
      }),
    [prefectureRegionMap]
  );

  const genreOptions = useMemo(() => {
    if (!Array.isArray(genres)) return [];
    return genres
      .map((genre) => {
        const rawValue = genre?.value ?? genre?.id;
        if (rawValue === undefined || rawValue === null || rawValue === '') return null;
        return {
          value: normalizeSelectionValue(rawValue),
          label: genre.label || genre.name || normalizeSelectionValue(rawValue),
        };
      })
      .filter(Boolean)
      .sort((a, b) => a.label.localeCompare(b.label, 'ja'));
  }, [genres]);

  const selectedRegionPrefectures = useMemo(() => {
    const set = new Set();
    filterRegions.forEach((region) => {
      (regionMapping[region] || []).forEach((pref) => set.add(pref));
    });
    return set;
  }, [filterRegions]);

  const toggleGenreSelection = useCallback((value) => {
    const normalized = normalizeSelectionValue(value);
    if (!normalized) return;
    setFilterGenres((prev) => {
      const prevNormalized = normalizeSelectionArray(prev);
      return prevNormalized.includes(normalized)
        ? prevNormalized.filter((v) => v !== normalized)
        : [...prevNormalized, normalized];
    });
  }, []);

  const toggleRegionSelection = useCallback((value) => {
    const normalized = normalizeSelectionValue(value);
    if (!normalized) return;
    setFilterRegions((prev) => {
      const prevNormalized = normalizeSelectionArray(prev);
      const exists = prevNormalized.includes(normalized);
      const next = exists
        ? prevNormalized.filter((v) => v !== normalized)
        : [...prevNormalized, normalized];
      setFilterPrefectures((prevPref) => {
        const prevPrefNormalized = normalizeSelectionArray(prevPref);
        if (!exists) {
          const set = new Set(prevPrefNormalized);
          (regionMapping[normalized] || []).forEach((pref) => set.add(pref));
          return Array.from(set);
        }
        const removal = regionMapping[normalized] || [];
        return prevPrefNormalized.filter((pref) => !removal.includes(pref));
      });
      return next;
    });
  }, []);

  const syncRegionsWithPrefectures = useCallback((nextPrefectures) => {
    const normalizedPrefectures = normalizeSelectionArray(nextPrefectures);
    setFilterRegions((prevRegions) =>
      normalizeSelectionArray(prevRegions).filter((region) => {
        const prefs = regionMapping[region] || [];
        return prefs.some((pref) => normalizedPrefectures.includes(pref));
      })
    );
  }, []);

  const updatePrefectures = useCallback(
    (updater) => {
      setFilterPrefectures((prev) => {
        const prevNormalized = normalizeSelectionArray(prev);
        const nextRaw = typeof updater === 'function' ? updater(prevNormalized) : updater;
        const nextNormalized = normalizeSelectionArray(nextRaw);
        syncRegionsWithPrefectures(nextNormalized);
        return nextNormalized;
      });
    },
    [syncRegionsWithPrefectures]
  );

  const togglePrefectureSelection = useCallback(
    (value) => {
      const normalized = normalizeSelectionValue(value);
      if (!normalized) return;
      updatePrefectures((prev) =>
        prev.includes(normalized) ? prev.filter((v) => v !== normalized) : [...prev, normalized]
      );
    },
    [updatePrefectures]
  );

  const removeGenreSelection = useCallback((value) => {
    const normalized = normalizeSelectionValue(value);
    setFilterGenres((prev) => normalizeSelectionArray(prev).filter((v) => v !== normalized));
  }, []);

  const removeRegionSelection = useCallback((value) => {
    const normalized = normalizeSelectionValue(value);
    setFilterRegions((prev) =>
      normalizeSelectionArray(prev).filter((region) => region !== normalized)
    );
    setFilterPrefectures((prev) => {
      const prevNormalized = normalizeSelectionArray(prev);
      const removal = new Set(regionMapping[normalized] || []);
      return prevNormalized.filter((pref) => !removal.has(pref));
    });
  }, []);

  const removePrefectureSelection = useCallback(
    (value) => {
      const normalized = normalizeSelectionValue(value);
      updatePrefectures((prev) => prev.filter((pref) => pref !== normalized));
    },
    [updatePrefectures]
  );

  const removeVenueSelection = useCallback((venueId) => {
    const normalized = normalizeSelectionValue(venueId);
    setFilterVenues((prev) =>
      normalizeSelectionArray(prev).filter((v) => v !== normalized)
    );
  }, []);

  const closeGenreFilterPopup = useCallback(() => {
    setShowGenreFilterPopup(false);
    setGenreFilterSearch('');
  }, []);

  const closeRegionFilterPopup = useCallback(() => {
    setShowRegionFilterPopup(false);
  }, []);

  const closePrefectureFilterPopup = useCallback(() => {
    setShowPrefectureFilterPopup(false);
    setPrefectureFilterSearch('');
  }, []);

  return {
    allVenues,
    autoRange,
    closeGenreFilterPopup,
    closePrefectureFilterPopup,
    closeRegionFilterPopup,
    filterEndDate,
    filterEventName,
    filterGenres,
    filterPrefectures,
    filterRegions,
    filterStartDate,
    filterVenues,
    genreFilterSearch,
    genreMap,
    genreOptions,
    prefectureFilterSearch,
    prefectureOptions,
    regionLabelMap,
    regionSelectionOptions,
    selectedRegionPrefectures,
    setAllVenues,
    setAutoRange,
    setFilterEndDate,
    setFilterEventName,
    setFilterGenres,
    setFilterRegions,
    setFilterStartDate,
    setFilterVenues,
    setGenreFilterSearch,
    setPrefectureFilterSearch,
    setShowGenreFilterPopup,
    setShowPrefectureFilterPopup,
    setShowRegionFilterPopup,
    setShowSearchPopup,
    setShowVenueFilterPopup,
    setSortOrder,
    setVenueFilterPrefecture,
    setVenueFilterSearch,
    showGenreFilterPopup,
    showPrefectureFilterPopup,
    showRegionFilterPopup,
    showSearchPopup,
    showVenueFilterPopup,
    sortOrder,
    toggleGenreSelection,
    togglePrefectureSelection,
    toggleRegionSelection,
    updatePrefectures,
    removeGenreSelection,
    removePrefectureSelection,
    removeRegionSelection,
    removeVenueSelection,
    venuesById,
    venueFilterPrefecture,
    venueFilterSearch,
  };
}
